
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderAwsApiGatewayGuide () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">
                Netraa APM
              </a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Cloud</span>
              <FiChevronRight />
              <span>AWS</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">AWS Gateway</span>
            </nav>
    
            {/* Intro */}
            <div className="mb-10 scroll-mt-24" id="overview">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  AWS API Gateway Monitoring Setup Guide
                </span>
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This document explains how to enable AWS API Gateway monitoring and stream CloudWatch metrics to Netraa APM using Amazon Kinesis Data Firehose and CloudWatch Metric Streams.
              </p>
            </div>
    
            {/* Overview */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">insights</span>
                </span>
                Overview
              </h2>
              <ol className="list-decimal pl-5 space-y-3 text-text-muted dark:text-gray-400">
                <li>Enable CloudWatch metrics for AWS API Gateway.</li>
                <li>
                  Create a Kinesis Data Firehose Delivery Stream and configure an HTTPS Destination pointing to:
                  <CodeBlock code={`https://netraaapm.ap2l.ai/aws/firehose/metrics`} />
                </li>
                <li>Create a CloudWatch Metric Stream and route AWS/GATEWAY metrics into the Firehose delivery stream.</li>
                <li>Select the OpenTelemetry 0.7 output format.</li>
              </ol>
            </section>
    
            {/* Prerequisites */}
            <section className="mb-12 scroll-mt-24" id="prerequisites">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">check_circle</span>
                </span>
                Prerequisites
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>AWS account with permissions:
                  <ul className="list-disc pl-5 mt-1">
                    <li>cloudwatch:PutMetricStream</li>
                    <li>firehose:CreateDeliveryStream</li>
                    <li>firehose:PutRecord</li>
                  </ul>
                </li>
                <li>Existing API Gateway instance(s)</li>
                <li>Internet connectivity for Firehose to send data to the Netraa APM HTTPS endpoint</li>
              </ul>
            </section>
    
            {/* Step 1 */}
            <section className="mb-12 scroll-mt-24" id="steps">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
                  <span className="material-symbols-outlined text-[24px]">settings</span>
                </span>
                Step 1: Enable CloudWatch Metrics
              </h2>
              <p className="text-text-muted dark:text-gray-400 mb-2">
                CloudWatch metrics are enabled by default for API Gateway. To verify:
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>Go to AWS Console → API Gateway</li>
                <li>Select your API</li>
                <li>Go to <strong>Stages → (your stage)</strong></li>
                <li>Open the <strong>Monitoring</strong> tab</li>
              </ol>
              <p className="text-text-muted dark:text-gray-400 mt-2">
                Confirm metrics such as: <code>Count</code>, <code>Latency</code>, <code>IntegrationLatency</code>, <code>4XXError</code>, <code>5XXError</code>, <code>CacheHitCount</code> / <code>CacheMissCount</code>
              </p>
    
              <h3 className="text-xl font-semibold text-text-main dark:text-white mt-4 mb-2">Enable Detailed Metrics (Recommended)</h3>
              <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>Go to <strong>Stages → Edit</strong></li>
                <li>Enable:
                  <ul className="list-disc pl-5 mt-1">
                    <li>Detailed CloudWatch Metrics</li>
                    <li>Logging (optional but recommended)</li>
                  </ul>
                </li>
                <li>Deploy the stage</li>
              </ol>
            </section>
    
            {/* Step 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">cloud</span>
                </span>
                Step 2: Create a Kinesis Data Firehose Delivery Stream
              </h2>
              <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>Go to AWS Console → Kinesis → Data Firehose</li>
                <li>Click Create delivery stream</li>
                <li>Configure:
                  <ul className="list-disc pl-5 mt-1">
                    <li>Source: Direct PUT</li>
                    <li>Destination: HTTP Endpoint</li>
                  </ul>
                </li>
                <li>Set HTTP destination URL:
                  <CodeBlock code={`https://netraaapm.ap2l.ai/aws/firehose/metrics`} />
                </li>
                <li>Disable data transformation</li>
                <li>Create the delivery stream</li>
              </ol>
            </section>
    
            {/* Step 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">stream</span>
                </span>
                Step 3: Create a CloudWatch Metric Stream
              </h2>
              <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>Go to AWS Console → CloudWatch → Metric Streams</li>
                <li>Click Create metric stream</li>
                <li>Select output format: OpenTelemetry 0.7</li>
                <li>Choose namespaces: AWS/API_GATEWAY</li>
                <li>Select the Firehose delivery stream created in Step 2</li>
                <li>Optionally select all API Gateway instances</li>
                <li>Review and create the metric stream</li>
              </ol>
            </section>
    
            {/* Data Flow */}
            <section className="mb-12 scroll-mt-24" id="data-flow">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
                  <span className="material-symbols-outlined text-[24px]">device_hub</span>
                </span>
                Data Flow
              </h2>
              <CodeBlock code={`AWS API GATEWAY → CloudWatch Metrics → CloudWatch Metric Stream → Kinesis Firehose → HTTPS Endpoint → Netraa APM`} />
            </section>
    
            {/* Completion */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">done_all</span>
                </span>
                Completion
              </h2>
              <p className="text-text-muted dark:text-gray-400">
                After configuration, metrics typically begin streaming into Netraa APM within 30–60 seconds.
              </p>
            </section>
          </div>
        </main>
      </div>
    );
}